import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Category } from '@app/core/model/other/classification';
import { FieldConfig } from '@app/core/model/other/field-config';
import { AbstractFieldGroupBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { FieldAnchorDirective } from '@app/shared/components/fields/field-anchor.directive';
import { Dimensions } from '@app/shared/extra/column-dimensions.enum';
import { ColumnType } from '@app/shared/extra/column-type.enum';
import { getValue } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { ClassificationService } from '@app/shared/services/classification.service';
import { ValidationService } from '@app/shared/services/validation.service';
import { TranslateService } from '@ngx-translate/core';
import {
  GetMainMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowSelectedEvent,
  SelectionChangedEvent
} from 'ag-grid-community';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Component({
  selector: 'technical-category-field-builder',
  templateUrl: './classification-field-builder.component.html',
  styleUrls: ['./classification-field-builder.component.scss'],
  providers: [ClassificationService],
  standalone: false
})
export class ClassificationFieldBuilderComponent extends AbstractFieldGroupBuilder implements OnInit, AfterViewInit {

  @ViewChild(FieldAnchorDirective, {static: true}) public fieldHost: FieldAnchorDirective;
  @ViewChild('gridContainer') public gridContainer: ElementRef;

  public gridOptions: GridOptions;
  public gridApi: GridApi;
  public fields: { id: string, label: string, value: string }[] = [];
  private lastSelectedRowNode: any;
  private forceSelection: boolean;

  protected validationService = inject(ValidationService);
  private translate = inject(TranslateService);
  private columnBuilder = inject(ColumnBuilder);
  private classificationService = inject(ClassificationService);
  private gridOptionsService = inject(GridOptionsService);

  /**
   * Init grid options.
   */
  constructor() {
    super();

    const extraGridOptions: GridOptions = {
      getRowId: (params): string => params.data.id,
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
        enableSelectionWithoutKeys: true
      },
      treeData: true,
      getDataPath: (data: Category): string[] => data.path,
      suppressContextMenu: true,
      animateRows: true,
      context: this,
      overlayLoadingTemplate: '<span class="ag-overlay-loading-center">' + this.translate.instant('LABEL.LOADING') + '</span>',
      suppressMovableColumns: true,
      localeText: this.columnBuilder.localeTextCommon(),
      autoGroupColumnDef: {
        headerName: this.fieldGroup.label,
        valueGetter: ({data}: { data: Category }): string => data?.label,
        tooltipValueGetter: ({data}: { data?: Category }): string => data?.label,
        colId: 'ag-Grid-AutoColumn',
        cellClass: ColumnType.TEXT,
        width: Dimensions.XL,
        cellRendererParams: {suppressCount: true}
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        if (!this.forceSelection) return;
        const api = event.api;
        const selectedNodes = api.getSelectedNodes();

        // If a rowNode is selected, then save it as lastSelectedRowNode,
        // Otherwise if row is unselected, force selection of the lastSelectedRowNode
        if (selectedNodes.length > 0) {
          this.lastSelectedRowNode = selectedNodes[0];
        } else if (this.lastSelectedRowNode) {
          this.lastSelectedRowNode.setSelected(true);
        }
      },
      getMainMenuItems: (params: GetMainMenuItemsParams): string[] => params.defaultItems,
      getRowHeight: (): number => this.gridOptionsService.staticGridOptions.rowHeight,
      onRowSelected: this.onRowSelected.bind(this),
      onGridSizeChanged: (): void => {
        this.gridApi.sizeColumnsToFit();
      }
    };
    this.gridOptions = {
      ...this.gridOptionsService.staticGridOptions,
      ...extraGridOptions
    };
  }

  /**
   * Init FormStateService value and load Organization Classification.
   */
  public ngOnInit(): void {
    // Cannot edit this field's group if the user has no write access to some fields in this group
    this.preconditionsForEdition &&= this.fieldGroup.children.every(field =>
      this.accessManager.hasFieldWriteAccess(field.code, field.fieldEntityType)
    );

    this.setFieldGroupInfo([], {
      state: new ReplaySubject<FieldStateMode>(1),
      isDisplayed: new BehaviorSubject<boolean>(false),
      isSingleField: false,
      value: this.initialValue,
      initialValue: this.initialValue
    });
    this.fields = this.getFieldsValues();
    this.getNextState();
  }

  /**
   * Setup hooks and listen for state change.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();

    this.getFieldGroupState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(newMode => {
      if (newMode === FieldStateMode.AFTER_SAVE) {
        this.refreshEntity();
        this.setFieldGroupValue(this.initialValue);
        this.setFieldGroupInitialValue(this.getFieldGroupValue());
        this.fields = this.getFieldsValues();
        this.getNextState();
      } else {
        this.currentMode = newMode;
      }
    });

    // If at least one member of the group is not computed, the group must be displayed
    if (this.fieldGroup.children.some(fieldConfig => !fieldConfig.computed)) {
      this.getFieldGroupInfo(['isDisplayed']).next(true);
    }
  }

  /**
   * Auto-size column and select row corresponding to the category if it is present.
   * @param params Ag-grid params.
   */
  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('loading', true);
    this.classificationService.getClassificationByCode(this.fieldGroup.classificationCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(categories => {
        const currentValue = this.getFieldGroupValue();

        // We get computed categories, they should be in categories path, otherwise they're not supposed to be shown
        const computedCategories = this.fieldGroup.children.filter(fc => fc.computed)
          .map(fc => categories.find(c => currentValue[fc.fieldCode] === c.label).id);
        const filteredCategories = computedCategories.length ? categories.filter(c => {
          return computedCategories.every(compCat => c.path.includes(compCat));
        }) : categories;
        this.gridApi.setGridOption('rowData', filteredCategories);

        // Disable deselection if at least one level of category is computed
        this.forceSelection = !!computedCategories.length;

        let currentPath = [];
        const currentValueKeys = Object.keys(currentValue);

        // Expand and select current value
        this.fieldGroup.children.filter(fc => currentValueKeys.includes(fc.fieldCode))
          .sort((fc1, fc2) => (
            fc1.classificationLevel.compareTo(fc2.classificationLevel)
          ))
          .forEach(({classificationLevel, fieldCode}) => {
            const value = currentValue[fieldCode];
            const categoryToExpandOrSelect = filteredCategories.find(category => {
              return category.label === value && category.path.length === classificationLevel
                && (currentPath.length === 0 || currentPath.every(path => category.path.includes(path)));
            });
            if (classificationLevel === currentValueKeys.length) {
              // Select category
              this.gridApi.getRowNode(categoryToExpandOrSelect.id).setSelected(true);
            } else {
              currentPath = categoryToExpandOrSelect.path;
              // Expand parent category
              this.gridApi.getRowNode(categoryToExpandOrSelect.id).setExpanded(true);
            }
          });
        this.gridApi.sizeColumnsToFit();
        categories.length ? this.gridApi.hideOverlay() : this.gridApi.showNoRowsOverlay();
      });
  }

  /**
   * Update the FieldGroup's value in the FormStateService whenever a row is selected.
   * @param params Ag-grid selection event.
   */
  public onRowSelected(params: RowSelectedEvent): void {
    if (params.node.isSelected()) {
      const category = params.node.data as Category;
      this.setFieldGroupValue(this.convertPathToCategories(category.path));
    } else if (this.gridApi.getSelectedRows().length === 0) {
      this.setFieldGroupValue('');
    }
  }

  /**
   * Get the config of a category level field.
   * @param fieldCode Field's code.
   * @return Field's config.
   */
  public getFieldConfig(fieldCode: string): FieldConfig {
    return this.fieldGroup.children.find((fieldConfig) => fieldConfig.fieldCode === fieldCode);
  }

  /**
   * Switch field to edit mode then focus and scroll element into view.
   */
  public edit(): void {
    super.edit();

    // Focus and scroll to field
    setTimeout(() => {
      this.gridContainer.nativeElement.focus();
      this.gridContainer.nativeElement.scrollIntoView({behavior: 'instant', block: 'center'});
    });
  }

  /**
   * Fetch the initial value of each field of the fieldGroup.
   * @return Object containing initial value of each entity field.
   * @private
   */
  private get initialValue(): { [k: string]: string } {
    return Object.fromEntries(
      this.fieldGroup.children.reduce((prev, fc) => {
        const value = getValue(this.entity, fc.fieldPath);
        if (!value) return prev;
        prev.push([fc.fieldCode, value]);
        return prev;
      }, [])
    );
  }

  /**
   * Match category ids with category keys of the entity.
   * @param path List of Category ids.
   * @return Object containing entity field and the category label for each id.
   * @private
   */
  private convertPathToCategories(path: string[]): { [k: string]: string } {
    // Assign category levels or use default value.
    const pathCategories = path.reduce((categories, id, level) => {
      const category = this.classificationService.categories.find(category => category.id === id);
      const field = this.fieldGroup.children.find(f => f.classificationLevel === level + 1);
      categories[field.fieldCode] = category.label;
      return categories;
    }, {} as any);
    return {
      ...Object.fromEntries(
        this.fieldGroup.children.map(fc => fc.fieldCode).map(fieldCode => [fieldCode, ''])
      ),
      ...pathCategories
    };
  }

  /**
   * Get the label and value of each subfield of the FieldGroup.
   * @return Fields' values.
   * @private
   */
  private getFieldsValues(): { id: string, label: string, value: string }[] {
    return Object.entries(this.getFieldGroupValue())
      .map(([label, value]: [string, string]) => ({id: crypto.randomUUID(), label, value}));
  }
}
